import { useCallback, useRef, useState } from "react";
import useValidatorStore from "./useValidatorStore";
import {
  getComponentValidator,
  getRegistryValidators,
  getValidatorRequired,
} from "@/components/form/attributes/validator/definition";

type ValidatorConfig = {
  required?: boolean;
  componentValidator?: {
    code?: string;
    errorMessage?: string;
    enableApi?: boolean;
  };
  registryValidators?: { validatorId: string; enableApi?: boolean }[];
  code?: string;
  isApi?: boolean;
};

type ValidateAndCommitOptions<TValue> = {
  value: TValue;
  setValue?: (value: TValue) => void;
  commitMode?: "before" | "after";
  validator?: ValidatorConfig;
  defaultErrorMessage?: string;
  isRequiredInvalid?: (value: TValue) => boolean;
  requiredMessage?: string;
  readonly?: boolean;
  onValidationSuccess?: () => void;
};

export function useFieldValidationState(entityId: string) {
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const [isValidating, setIsValidating] = useState(false);
  const runIdRef = useRef(0);
  const {
    executeRegistryValidator: getCodeValidatorError,
    executeLocalValidator,
  } = useValidatorStore();

  const validateAndCommit = useCallback(
    async <TValue>({
      value,
      setValue,
      commitMode = "before",
      validator,
      defaultErrorMessage = "Validation failed",
      isRequiredInvalid,
      requiredMessage = "This field is required",
      readonly,
      onValidationSuccess,
    }: ValidateAndCommitOptions<TValue>): Promise<string | undefined> => {
      if (readonly) {
        setValue?.(value);
        return undefined;
      }

      if (commitMode === "before") {
        setValue?.(value);
      }

      const runId = ++runIdRef.current;
      let nextError: string | undefined;

      if (isRequiredInvalid?.(value)) {
        nextError = requiredMessage;
      } else {
        const registryValidatorConfigs = getRegistryValidators(validator);
        const registryUsesApi = registryValidatorConfigs.length > 0;
        const componentValidator = getComponentValidator(validator);
        const validatorRequired = getValidatorRequired(validator);
        const localUsesApi = Boolean(
          componentValidator?.enableApi && componentValidator?.code,
        );
        const shouldShowValidating = Boolean(
          validatorRequired && (registryUsesApi || localUsesApi),
        );

        if (shouldShowValidating) {
          setIsValidating(true);
        }

        try {
          const registryError = await getCodeValidatorError({
            required: validatorRequired,
            entityId,
            value: value ?? "",
            registryValidatorConfigs,
          });
          const localValidatorError = await executeLocalValidator({
            entityId,
            required: validatorRequired,
            validatorCode: componentValidator?.code,
            defaultErrorMessage:
              componentValidator?.errorMessage ?? defaultErrorMessage,
            value,
            isApi: componentValidator?.enableApi,
          });

          if (runId !== runIdRef.current) {
            return undefined;
          }

          nextError = registryError ?? localValidatorError ?? undefined;
        } finally {
          if (runId === runIdRef.current) {
            setIsValidating(false);
          }
        }
      }

      if (runId === runIdRef.current) {
        setLocalError(nextError);
        if (!nextError) {
          onValidationSuccess?.();
        }
      }

      if (commitMode === "after") {
        setValue?.(value);
      }

      return nextError;
    },
    [entityId, executeLocalValidator, getCodeValidatorError],
  );

  return {
    localError,
    isValidating,
    validateAndCommit,
    setLocalError,
  };
}
